<template>
  <v-card flat>
    <v-card-title class="d-flex align-center px-4 py-3">
      <div class="d-flex align-center flex-grow-1">
        <div class="d-flex align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                @click="changeMonth(-1)"
                class="mr-1"
              >
                <v-icon size="24">mdi-chevron-left</v-icon>
              </v-btn>
            </template>
            <span>上個月</span>
          </v-tooltip>

          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                outlined
                label
                v-bind="attrs"
                v-on="on"
                class="px-3"
                color="primary"
              >
                <v-icon left size="16">mdi-calendar</v-icon>
                {{ dateRangeText }}
              </v-chip>
            </template>
            <v-date-picker
              v-model="selectedMonth"
              type="month"
              no-title
              scrollable
              @input="dateMenu = false"
              color="blue"
              locale="zh-TW"
            >
            </v-date-picker>
          </v-menu>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                @click="changeMonth(1)"
                class="ml-1 mr-4"
              >
                <v-icon size="24">mdi-chevron-right</v-icon>
              </v-btn>
            </template>
            <span>下個月</span>
          </v-tooltip>
        </div>

        <v-btn-toggle
          color="blue"
          v-model="statusFilter"
          dense
          class="mr-4 status-toggle"
          mandatory
          @change="onStatusChange"
        >
          <v-btn
            v-for="option in statusOptions"
            :key="option.text"
            :value="option.value"
            outlined
            class="text-capitalize"
          >
            {{ option.text }}
          </v-btn>
        </v-btn-toggle>
      </div>

      <v-btn
        icon
        small
        @click="fetchAnomalies"
        :loading="loading"
        color="grey darken-1"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-0">
      <v-data-table
        :headers="headers"
        :items="anomalies"
        :loading="loading"
        :items-per-page="itemsPerPage"
        disable-pagination
        class="table elevation-0"
        hide-default-footer
        :no-data-text="loading ? '讀取中...' : '目前無紀錄'"
      >
        <template v-slot:item.user_name="{ item }">
          <div class="d-flex align-center">
            <span class="text-sm font-weight-normal text-body">
              {{ item.user ? item.user.name : "-" }}
            </span>
          </div>
        </template>

        <template v-slot:item.clock_date="{ item }">
          <div class="d-flex align-center">
            <span class="text-sm font-weight-normal text-body">
              {{ item.clock_date }}
            </span>
          </div>
        </template>
        <template v-slot:item.clock_in_time="{ item }">
          <div class="d-flex align-center">
            <span class="text-sm font-weight-normal text-body">
              {{ formatScheduledTime(item.clock_in_time) || "-" }}
            </span>
          </div>
        </template>
        <!-- clock_in_actual -->
        <template v-slot:item.clock_in_actual="{ item }">
          <div class="d-flex align-center">
            <div>
              <span
                :class="{ 'red--text': isLate(item) }"
                class="text-sm font-weight-normal text-body"
              >
                {{ formatTimeOnly(item.clock_in_actual) || "-" }}
              </span>
              <div v-if="isLate(item)" class="text-caption red--text">
                遲到 {{ item.late }} 分鐘
              </div>
            </div>
          </div>
        </template>
        <!-- clock_out_time -->
        <template v-slot:item.clock_out_time="{ item }">
          <div class="d-flex align-center">
            <span class="text-sm font-weight-normal text-body">
              {{ formatScheduledTime(item.clock_out_time) || "-" }}
            </span>
          </div>
        </template>
        <!-- clock_out_actual -->
        <template v-slot:item.clock_out_actual="{ item }">
          <div class="d-flex align-center">
            <div>
              <span
                :class="{ 'red--text': isEarly(item) }"
                class="text-sm font-weight-normal text-body"
              >
                {{ formatTimeOnly(item.clock_out_actual) || "-" }}
              </span>
              <div v-if="isEarly(item)" class="text-caption red--text">
                早退 {{ item.early }} 分鐘
              </div>
            </div>
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <div class="d-flex align-center gap-1">
            <template v-for="s in getStatusArray(item)">
              <v-tooltip
                v-if="
                  s === AttendanceStatus.LEAVE_CLOCK_IN &&
                  item.extra &&
                  (item.extra.re_clock_in || item.extra.re_clock_out)
                "
                :key="'hint-' + s"
                bottom
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    v-bind="attrs"
                    v-on="on"
                    small
                    :color="getStatusColor(s)"
                    text-color="white"
                    class="px-2"
                  >
                    {{ getStatusText(s) }}
                  </v-chip>
                </template>
                <span>
                  {{
                    item.extra.re_clock_in
                      ? "打卡上班: " + formatTimeOnly(item.extra.re_clock_in)
                      : ""
                  }}
                  {{
                    item.extra.re_clock_out
                      ? "打卡下班: " + formatTimeOnly(item.extra.re_clock_out)
                      : ""
                  }}
                </span>
              </v-tooltip>
              <v-chip
                v-else
                :key="s"
                small
                :color="getStatusColor(s)"
                text-color="white"
                class="px-2"
              >
                {{ getStatusText(s) }}
              </v-chip>
            </template>
          </div>
        </template>
        <!-- actions -->
        <template v-slot:item.actions="{ item }">
          <v-btn
            v-if="
              statusFilter == AttendanceStatus.ABNORMAL &&
              getStatusArray(item).includes(AttendanceStatus.LEAVE_CLOCK_IN)
            "
            icon
            small
            @click="adjustLeave(item)"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- Adjust Leave Dialog -->
    <v-dialog v-model="adjustmentDialog" max-width="600px">
      <v-card class="border-radius-lg">
        <v-card-title class="px-6 py-4">
          <span class="text-h6 font-weight-bold">調整請假期間打卡</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeAdjustmentDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="px-6 py-4">
          <v-list dense class="mb-4 pa-0" v-if="editedItem">
            <v-list-item class="px-0">
              <v-list-item-content>
                <div class="text-caption grey--text mb-1">員工</div>
                <div class="font-weight-bold">
                  {{ editedItem.user ? editedItem.user.name : "-" }}
                </div>
              </v-list-item-content>
              <v-list-item-content>
                <div class="text-caption grey--text mb-1">日期</div>
                <div class="font-weight-bold">{{ editedItem.clock_date }}</div>
              </v-list-item-content>
            </v-list-item>

            <v-list-item class="px-0 mt-2">
              <v-list-item-content>
                <div class="text-caption grey--text mb-1">請假區間</div>
                <div
                  v-for="leave in editedItem.leave_records || []"
                  :key="leave.id"
                  class="text-sm font-weight-bold"
                >
                  {{ formatDateTime(leave.start_time) }} -
                  {{ formatDateTime(leave.end_time) }}
                </div>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <div class="text-caption red--text mb-4">
            ※ 請假最小單位一小時，補打卡時間必須為整點。
          </div>

          <v-divider class="mb-6"></v-divider>

          <!-- Time Pickers -->
          <v-row v-if="editedItem">
            <v-col cols="6">
              <v-menu
                v-model="reClockInMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="editedItem.extra.re_clock_in_time"
                    label="補打上班卡"
                    prepend-icon="mdi-clock-outline"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-if="reClockInMenu"
                  v-model="editedItem.extra.re_clock_in_time"
                  full-width
                  @change="reClockInMenu = false"
                  format="24hr"
                  color="blue"
                  :allowed-minutes="(m) => m === 0"
                ></v-time-picker>
              </v-menu>
            </v-col>
            <v-col cols="6">
              <v-menu
                v-model="reClockOutMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="editedItem.extra.re_clock_out_time"
                    label="補打下班卡"
                    prepend-icon="mdi-clock-outline"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-if="reClockOutMenu"
                  v-model="editedItem.extra.re_clock_out_time"
                  full-width
                  @change="reClockOutMenu = false"
                  format="24hr"
                  color="blue"
                  :allowed-minutes="(m) => m === 0"
                ></v-time-picker>
              </v-menu>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="pb-6 px-6">
          <v-spacer></v-spacer>
          <v-btn
            class="
              font-weight-bold
              text-capitalize
              btn-secondary
              bg-gradient-light
              py-3
              px-6
            "
            text
            @click="closeAdjustmentDialog"
          >
            取消
          </v-btn>
          <v-btn
            class="
              font-weight-bold
              text-capitalize
              btn-primary
              bg-gradient-primary
              py-3
              px-6
            "
            @click="saveAdjustment"
            :loading="savingAdjustment"
          >
            確認
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="d-flex justify-center py-4">
      <v-pagination
        v-model="page"
        :length="pageCount"
        @input="fetchAnomalies"
        prev-icon="fa fa-angle-left"
        next-icon="fa fa-angle-right"
        color="#cb0c9f"
        circle
      ></v-pagination>
    </div>

    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" text @click="snackbar.show = false">關閉</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import {
  AttendanceStatus,
  getStatusText as getStatusTextUtil,
  getStatusColor as getStatusColorUtil,
} from "@/utils/attendance";
import {
  formatTimeOnly as formatTimeOnlyUtil,
  formatScheduledTime as formatScheduledTimeUtil,
  formatDateTime as formatDateTimeUtil,
} from "@/utils/time";

export default {
  name: "ClockAnomalyWidget",
  props: {
    selectedCompany: {
      type: Object,
      default: () => null,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const today = new Date().toISOString().substr(0, 7); // YYYY-MM
    return {
      loading: false,
      anomalies: [],
      dateMenu: false,
      selectedMonth: today,
      statusFilter: null,
      page: 1,
      pageCount: 0,
      itemsPerPage: 15,

      pagination: {
        currentPage: 1,
        pageSize: 15,
        totalItems: 0,
        totalPages: 1,
      },
      headers: [
        { text: "員工", value: "user_name", sortable: false },
        { text: "日期", value: "clock_date", sortable: false },
        { text: "表定上班", value: "clock_in_time", sortable: false },
        { text: "實際上班", value: "clock_in_actual", sortable: false },
        { text: "表定下班", value: "clock_out_time", sortable: false },
        { text: "實際下班", value: "clock_out_actual", sortable: false },
        { text: "狀態", value: "status", sortable: false },
        // actions
        // { text: "動作", value: "actions", sortable: false },
      ],
      AttendanceStatus: AttendanceStatus,

      // Adjustment Dialog
      adjustmentDialog: false,
      savingAdjustment: false,
      editedItem: null,
      reClockInMenu: false,
      reClockOutMenu: false,

      snackbar: {
        show: false,
        text: "",
        color: "success",
        timeout: 3000,
      },
    };
  },
  mounted() {
    this.fetchAnomalies();
  },
  computed: {
    dateRangeText() {
      return this.selectedMonth;
    },
    statusOptions() {
      return [
        // { text: "全部 (異常)", value: null },
        { text: "異常", value: AttendanceStatus.ABNORMAL },
        { text: "缺席", value: AttendanceStatus.ABSENT },
        { text: "遲到", value: AttendanceStatus.LATE },
        { text: "早退", value: AttendanceStatus.EARLY_LEAVE },
        { text: "未打上班卡", value: AttendanceStatus.LATE_IN },
        { text: "未打下班卡", value: AttendanceStatus.LATE_OUT },
      ];
    },
  },
  watch: {
    selectedCompany: {
      handler() {
        this.page = 1;
        this.fetchAnomalies();
      },
      immediate: true,
    },
    selectedMonth() {
      this.page = 1;
      this.fetchAnomalies();
    },
  },
  methods: {
    async fetchAnomalies() {
      if (!this.selectedCompany) return;
      this.loading = true;
      try {
        const [year, month] = this.selectedMonth.split("-");

        const qp = {
          company_id: this.selectedCompany.id,
          year: year,
          month: parseInt(month),
          page: this.page,
          limit: this.itemsPerPage,
        };

        if (this.statusFilter != null) {
          qp.status = this.statusFilter;
        } else {
          return;
        }

        const queryParams = new URLSearchParams(qp);
        const response = await this.$axios.get(
          `/api/clock-records/company/monthly-abnormal?${queryParams}`
        );

        this.anomalies = response.data.rows || [];
        this.pagination = response.data.pagination;
        if (this.pagination) {
          this.pageCount = this.pagination.totalPages || 0;
        }
      } catch (error) {
        console.error("Error fetching anomalies:", error);
      } finally {
        this.loading = false;
      }
    },
    onStatusChange() {
      this.page = 1;
      this.fetchAnomalies();
    },

    formatTimeOnly(dateTime) {
      return formatTimeOnlyUtil(dateTime);
    },
    formatScheduledTime(timeStr) {
      return formatScheduledTimeUtil(timeStr);
    },
    formatDateTime(value) {
      return formatDateTimeUtil(value);
    },
    getStatusArray(record) {
      if (Array.isArray(record.status) && record.status.length > 0) {
        return record.status;
      }
      if (record.status != null) {
        return [record.status];
      }
      return [AttendanceStatus.ABNORMAL];
    },
    getStatusText(status) {
      return getStatusTextUtil(status);
    },
    getStatusColor(status) {
      return getStatusColorUtil(status);
    },
    isLate(item) {
      return (
        item.status === AttendanceStatus.LATE || (item.late && item.late > 0)
      );
    },
    isEarly(item) {
      return (
        item.status === AttendanceStatus.EARLY_LEAVE ||
        (item.early && item.early > 0)
      );
    },
    changeMonth(offset) {
      const [year, month] = this.selectedMonth.split("-").map(Number);
      const date = new Date(year, month - 1 + offset, 1);
      const newYear = date.getFullYear();
      const newMonth = String(date.getMonth() + 1).padStart(2, "0");
      this.selectedMonth = `${newYear}-${newMonth}`;
    },
    adjustLeave(item) {
      this.editedItem = JSON.parse(JSON.stringify(item));
      // Ensure extra object exists
      if (!this.editedItem.extra) {
        this.editedItem.extra = {};
      }
      // Prepare time strings for pickers (HH:mm)
      this.editedItem.extra.re_clock_in_time = this.extractTimePart(
        this.editedItem.extra.re_clock_in
      );
      this.editedItem.extra.re_clock_out_time = this.extractTimePart(
        this.editedItem.extra.re_clock_out
      );
      this.adjustmentDialog = true;
    },
    extractTimePart(isoString) {
      if (!isoString) return "";
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return "";
      const h = String(date.getHours()).padStart(2, "0");
      const m = String(date.getMinutes()).padStart(2, "0");
      return `${h}:${m}`;
    },
    closeAdjustmentDialog() {
      this.adjustmentDialog = false;
      this.editedItem = null;
    },
    async saveAdjustment() {
      if (!this.editedItem) return;

      const inTime = this.editedItem.extra.re_clock_in_time;
      const outTime = this.editedItem.extra.re_clock_out_time;

      const isWholeHour = (time) => !time || time.endsWith(":00");

      if (!isWholeHour(inTime) || !isWholeHour(outTime)) {
        this.showSnackbar("補打卡時間必須以小時為單位（整點）", "error");
        return;
      }

      this.savingAdjustment = true;
      try {
        // Construct ISO strings back from HH:mm using the clock_date
        const combine = (dateStr, timeStr) => {
          if (!timeStr) return null;
          return `${dateStr}T${timeStr}:00.000Z`; // Note: using Simple UTC for placeholder, backend should handle timezone
        };

        const payload = {
          clock_record_id: this.editedItem.id,
          re_clock_in: combine(
            this.editedItem.clock_date,
            this.editedItem.extra.re_clock_in_time
          ),
          re_clock_out: combine(
            this.editedItem.clock_date,
            this.editedItem.extra.re_clock_out_time
          ),
        };

        // Placeholder for the adjust leave API
        await this.$axios.post(
          `/api/clock-records/adjust-leave-clock`,
          payload
        );

        this.showSnackbar("調整成功", "success");
        this.closeAdjustmentDialog();
        this.fetchAnomalies();
      } catch (error) {
        console.error("Error adjusting leave clock:", error);
        const errorMsg =
          error.response?.data?.message || "調整失敗，請稍後再試";
        this.showSnackbar(errorMsg, "error");
      } finally {
        this.savingAdjustment = false;
      }
    },
    showSnackbar(text, type = "success") {
      this.snackbar.text = text;
      this.snackbar.color = type === "error" ? "error" : "success";
      this.snackbar.show = true;
    },
  },
};
</script>

<style scoped>
.table >>> thead th {
  white-space: nowrap;
  font-size: 1rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.025rem;
  color: #8392ab;
  opacity: 0.7;
  padding: 0.75rem 1.5rem;
  border-bottom: 0.0625rem solid #e9ecef;
}

.table >>> tbody tr:last-child td {
  border-bottom: 0;
}

.table >>> tbody td {
  padding: 0.75rem 1.5rem;
  border-bottom: 0.0625rem solid #e9ecef;
}

.table >>> tbody tr:hover {
  background: #f8f9fa;
}
</style>
